
export const API = "/api/v1/";
export const VIEW = "/view/";
export const ADMIN = "/admin/";
export const ACTION = "/action/";
export const ACTION_NOP = ACTION + "nop";
export const ID_PARAM = "/:id";
export const KEY_PARAM = "/:key";
// The tag pages address a tag by NAME, not by id or key: a name is what the
// reader clicked, in the tag cloud and on an entry's chips alike, and neither
// of those has anything else to hand.
export const TAG_PARAM = "/:tag";

export const NOT_FOUND_KEY = "notFound";

export const VIEW_HOME = "/";
// On /authUser, signed out too, only while esback opens the pages EsMore.js holds back (Talks and
// the devlog link). Its name says nothing of them: every browser that gets it reads it.
export const HDR_AUTH_MORE = "X-Es-More";
export const SECTION_HOME = "#home";
export const VIEW_REGISTER = VIEW + "register";
// The invitation code, carried in the registration page's own URL:
// /view/register?invite=shindig fills the box in for whoever followed the link.
//
// It is a browser query parameter and not an API one, which is why it sits here
// beside the route rather than with PAGE_PARAM and the rest further down. It is
// what the printed handout's QR code encodes, and what any "here is your code"
// link should carry: a code somebody has to retype off a card is a code half of
// them will mistype. It seeds the field and nothing else -- see EsRegister.js --
// so the value still travels in the POST body exactly as a typed one does, and
// a wrong or absent one is not an error at either end.
export const INVITE_PARAM = "invite";

// The address, carried the same way and for the same reason: the "Register for
// Escapade" link at the foot of a joiner's mailing opens the form with
// their address already in it. Somebody who has to retype an address off an
// email is somebody who mistypes it, and a mistyped one here would make a
// SECOND account rather than finishing the row they already have.
//
// It seeds the field and nothing else -- the value still travels in the POST
// body exactly as a typed one does, so a wrong or absent one is not an error at
// either end. esback builds the URL; see EsMailingService.registerUrl.
export const EMAIL_PARAM = "email";

// Why a federated sign-in did not end in an account, as esback spells it.
//
// Two and not one: a REFUSAL is escapade's decision about an identity that
// authenticated fine, and lands on the register page; a FAILURE is the OAuth
// exchange itself not completing, and lands back on the login page. The values
// are the server's -- EsOauthRegisterService.Outcome's reasons for one, Spring's
// OAuth2Error codes for the other -- and the sentences are in EsOauthMessage.
export const OAUTH_REFUSED_PARAM = "oauthRefused";
export const OAUTH_FAILED_PARAM = "oauthFailed";
export const VIEW_LOGIN = VIEW + "login";
export const VIEW_FORGOT = VIEW + "forgot";
// The birthdate step a login waits on when its account has none. See EsBirthdate.
export const VIEW_BIRTHDATE = VIEW + "birthdate";

// Where the link in a password-reset mail lands, carrying ?token=.
//
// ⚠️ esback BUILDS this URL and has to spell the same route and the same
// parameter name, exactly as it does for VIEW_OPT_OUT above. The two move
// together or every link already sitting in somebody's inbox 404s -- and unlike
// a mailing's opt-out, a reset link is short-lived, so a mismatch is found by
// the one person who cannot then get in, on the one link that will not still be
// there tomorrow.
export const VIEW_RESET = VIEW + "reset";

// Where a reader lands when they log in and their tier opens nothing yet. A
// successful login used to leave them on the marketing home page -- the same
// page they saw logged out -- which reads as the login having failed.
export const VIEW_WELCOME = VIEW + "welcome";
// The launch home page (2026-10-31): the tabletop release as it ships, beside the
// manifesto at /. Top level, which nginx's try_files and the ALB both allow, because
// every ad and post links it.
// --- changelog --------------------------------------------------------------
// Not under VIEW: the changelog was asked for at /changelog. nginx's try_files
// sends any unmatched path to index.html, and the ALB routes everything the
// esback patterns do not claim to esfront, so a bare top-level route works
// without touching either. In the main bundle, not EsMore's chunk: the Changelog
// is open to everyone, signed out too (Tim, 2026-10-31: "allow changelog to show
// in that flag, just not talks").
export const VIEW_CHANGELOG = "/changelog";
// The devlog, open to everyone with the Changelog (Tim, 2026-10-31: "devlog can be public like changelog").
export const DEVLOG_URL = "https://forums.tigsource.com/index.php?topic=74442.0";
export const API_RELEASE_LIST = API + "releaseList";
export const API_RELEASE_PAGE = API + "releasePage";
export const API_RELEASE_TOC = API + "releaseToc";

// --- legal / credits -------------------------------------------------------
// Top level rather than under VIEW, for the same reason as the changelog above:
// nginx's try_files sends any unmatched path to index.html and the ALB routes
// everything the esback patterns do not claim to esfront, so a bare /legal
// works without touching either. It is also the URL a licence notice is
// expected to live at, and it is linked from the footer of every page.
export const VIEW_LEGAL = "/legal";
// The privacy policy and the terms of service, top level for the same reason and
// because Stripe's account settings point at these two URLs: moving either breaks
// the live account's links. Reached from the legal page; the footer carries Legal only.
export const VIEW_PRIVACY = "/privacy";
export const VIEW_TERMS = "/terms";
// Every notice and licence text the open content needs, under Legal: reached from its hub.
export const VIEW_LICENSES = VIEW_LEGAL + "/licenses";
// Where Checkout does not sell: each makes a foreign seller appoint a resident tax
// representative from the first sale. Kept in step with EsStripeClient.NO_SALE.
export const NO_SALE_COUNTRIES = [ 'Albania', 'Bangladesh', 'Guinea', 'Mauritania', 'Mexico',
    'North Macedonia', 'Oman', 'Serbia', 'Tajikistan' ];
// A page an admin added in Crafter, by its slug (EsCrafterPage).
export const VIEW_PAGE = VIEW + "page";
// --- end legal / credits ---------------------------------------------------

// Your own account: the nickname people see, and your password.
//
// Under VIEW, and here that is load-bearing rather than a convention. EsNavBar
// swaps its left nav to the dash items only when
// `auth.authenticated && window.location.href.includes( VIEW )`, so a top-level
// "/account" -- spelled the way VIEW_CHANGELOG and VIEW_LEGAL are -- would make
// the nav revert to the marketing anchors the moment you landed on your own
// settings page. The characters constants above carry the long version.
//
// No flag gate. Every route behind rolloutEsCharacters or rolloutEsConsole
// is a feature that has not shipped; this is the screen that lets somebody who
// HAS registered change the name other people see them by, and the privacy half
// of the identity change (identity-plan §5) depends on it existing.
export const VIEW_ACCOUNT = VIEW + "account";
// The account's tabs ride in ?tab=; a value that is not a tab drawn falls back to Personal.
export const TAB_PERSONAL = "personal";
export const TAB_PASSWORD = "password";
export const TAB_SUBSCRIPTION = "subscription";
export const VIEW_ACCOUNT_SUBSCRIPTION = VIEW_ACCOUNT + "?tab=" + TAB_SUBSCRIPTION;
export const VIEW_THEME = VIEW + "colorsheet";

// Where the "Opt-out of Escapade mailings" link at the foot of every mailing
// lands.
//
// ⚠️ BEHIND RequireAuth, and the link carries NOTHING. A token in the URL would
// have let somebody opt out without signing in, which reads as considerate
// and is a hole: an email gets forwarded, and whoever received it could opt the
// original reader out silently. What gets opted out is the account you are
// signed in as. See EsOptOut.
//
// ⚠️ esback BUILDS this URL (EsMailingService.optOutUrl) and spells the route
// in its own EsConstants as VIEW_OPT_OUT. The two move together or every link
// already sitting in somebody's inbox 404s.
export const VIEW_OPT_OUT = VIEW + "optOut";
export const API_OPT_OUT = API + "optOut";

// The name of the query parameter a mailed link carries its credential in.
//
// TWO LINKS SPELL IT, and they are unrelated tokens with one thing in common:
// the person holding them cannot be asked to log in first.
//
// An OPT-OUT link carries one only for a JOINER. A registered reader's
// mailing has no token in it at all -- their opt-out is their session's -- so a
// forwarded copy gives whoever received it nothing to press. A mailing-list
// joiner has no Cognito user and can never sign in, so theirs carries one;
// without it they could never leave the list.
//
// A PASSWORD-RESET link always carries one, because not being able to log in is
// the whole reason it was asked for. That token is short-lived and single-use
// where the opt-out token is neither, and the two are never interchangeable
// -- what is shared here is the spelling of the parameter, so that VIEW_RESET
// and VIEW_OPT_OUT cannot drift apart from what esback puts in the mail.
export const TOKEN_PARAM = "token";

export const VIEW_ADMIN = VIEW + "admin";

export const VIEW_CODEX = VIEW + "codex";
export const VIEW_CODEX_TOC = VIEW + "codexToc";
export const VIEW_CODEX_SEARCH = VIEW + "codexSearch";
export const VIEW_CODEX_TAG_CLOUD = VIEW + "codexTagCloud";
// The signed-in account's bookmarked entries, beside the tag cloud.
export const VIEW_CODEX_BOOKMARKS = VIEW + "codexBookmarks";
// A sibling of the three above rather than VIEW_CODEX + "/:tag", which would
// have been the shorter spelling and the wrong one: VIEW_CODEX + KEY_PARAM is
// already the route for a codex entry, so the two would match the same URLs and
// a tag named like an entry key would open the entry.
export const VIEW_CODEX_TAG_ENTRIES = VIEW + "codexTagEntries";
// A section heading's own page, listing what is filed under it. Sibling of the
// other codex pages rather than VIEW_CODEX + KEY_PARAM, which already means
// "the entry with this key" -- and a parent section has no entry.
export const VIEW_CODEX_SECTION = VIEW + "codexSection";
// A new entry, written in the content pane before it has a key: ?pack=<id>&parent=<section key>.
export const VIEW_CODEX_NEW = VIEW + "codexNew";
// The rail's Packs tab: the pack list in the rail and the Library beside it, /:pack for one
// pack's page, /:pack/:key for an entry in it.
export const VIEW_PACKS = VIEW + "packs";
// The Library's old address, and its pack and entry pages', kept so a link still lands.
export const VIEW_LIBRARY = VIEW + "library";

// ---------------------------------------------------------------------------
// Characters -- a section beside the codex, behind rolloutEsCharacters.
//
// Under VIEW, and this is the one that would bite if it were not. EsNavBar
// swaps its left nav to the dash items only when
// `auth.authenticated && window.location.href.includes( VIEW )` -- so a
// top-level path like "/characters", spelled the way VIEW_CHANGELOG and
// VIEW_LEGAL are, would make the nav revert to the marketing links the moment
// you landed on it, and the Codex link would vanish from your own page. The
// changelog and the Legal page get away with it because they are marketing
// pages; this is not one.
// ---------------------------------------------------------------------------

// The list: every character this player owns, plus the way to make another.
export const VIEW_CHARACTERS = VIEW + "characters";

// One character's sheet, by id. ID_PARAM rather than KEY_PARAM because a
// character has no key: EsBean's key is unique across the table, and two
// players are each entitled to a "Thorin".
export const VIEW_CHARACTER_SHEET = VIEW + "characterSheet";

// The step-by-step builder. A sibling of the two above rather than
// VIEW_CHARACTERS + "/new", which would collide with a sheet route if the
// sheet ever moved under the list path, and reads as its own destination --
// which it is, since it is the tutorial as much as the form.
export const VIEW_CHARACTER_NEW = VIEW + "characterNew";

// ---------------------------------------------------------------------------
// Campaigns -- the Console, behind rolloutEsConsole.
//
// Under VIEW, for the reason spelled out over the character constants above:
// EsNavBar swaps its left nav to the dash items only when the URL contains
// VIEW, so a top-level "/campaigns" would revert the nav to the marketing
// anchors the moment you landed on it.
// ---------------------------------------------------------------------------

// The list: every campaign this player is a member of, plus the way to start
// another.
export const VIEW_CAMPAIGNS = VIEW + "campaigns";

// One campaign, by SLUG. A sibling of the list rather than a child, so the two
// never match the same URL -- and the route composes VIEW_CAMPAIGN + KEY_PARAM
// the way VIEW_CODEX + KEY_PARAM is composed in index.js. The ":key" is NOT
// baked in here: a constant carrying it cannot be used to build a link, which
// is the one thing a campaign constant is for.
export const VIEW_CAMPAIGN = VIEW + "campaign";

// The Console. Bare, it lists the consoles a reader can open (EsDash); with
// KEY_PARAM it is one campaign's console (EsGame), on the same SLUG the
// campaign page takes -- so /view/campaign/the-table and
// /view/console/the-table are the two things you can do with one table. The
// bare path and the keyed one never match the same URL.
//
// Behind rolloutEsConsole on this side, like every campaign route, and behind
// rolloutEsConsole on esback's side, which refuses every campaign path by
// stem. There was a narrower rolloutEsGame; es-0.6.0 removed it. See
// EsGameService.
export const VIEW_CONSOLE = VIEW + "console";
// The demo table (switchEsDemoMode): asks esback which campaign it is and opens its console.
// The stable address for "the demo", whatever the table's id is after a reset.
export const VIEW_DEMO = VIEW + "demo";
// The Google Cast receiver: the page a TV loads, registered as the Custom Receiver's URL in the
// Cast SDK Developer Console. Outside the shell; the TV signs in as nobody (EsCastReceiver).
export const VIEW_CAST = VIEW + "cast";

export const VIEW_ENTRY = VIEW + "entry";
export const VIEW_SECTION = VIEW + "section";
export const VIEW_COMMENT = VIEW + "comment";
export const VIEW_TAG = VIEW + "tag";

export const ADMIN_ACCOUNT_FORM = ADMIN + "accountForm";
// The one screen over es_account. There were two -- a CRUD screen that already
// held this name, and the roster beside it -- and they were collapsed on
// 2026-09-04: two menu buttons over one endpoint showing the same rows. The
// roster is what survived, and it took the other's Add button, its per-row
// delete and, on 2026-09-20, its name. It was ADMIN_PLAYER_LIST on
// "playerList" until then; the app's noun is "account". See the header of
// EsAccountList.
//
// ADMIN_ACCOUNT_FORM above is untouched: the form is still where a row is
// edited and where a new one is made.
export const ADMIN_ACCOUNT_LIST = ADMIN + "accountList";
// Alerts -- each event type the code raises, and where it is said: Slack, Discord, Pushover,
// and email. A list and a form, the rulesys' shape.
export const ADMIN_ALERT_LIST = ADMIN + "alertList";
export const ADMIN_ALERT_FORM = ADMIN + "alertForm";
// Copykeys and locale groups (rolloutEsTranslations): the site's words in every locale, and the
// groups their fallback chains run through. es-i18n.md, Admin screens.
export const ADMIN_COPYKEY_LIST = ADMIN + "copykeyList";
export const ADMIN_COPYKEY_FORM = ADMIN + "copykeyForm";
export const ADMIN_LOCALE_LIST = ADMIN + "localeList";
export const ADMIN_LOCALE_FORM = ADMIN + "localeForm";
// Translator recruitment (es-i18n.md, phase 8): locale groups ranked by demand against what
// they lack, each with its candidates to invite.
export const ADMIN_TRANSLATOR_LIST = ADMIN + "translatorList";
// Pushover's built-in sounds, as its API names them (pushover.net/api).
export const PUSHOVER_SOUNDS = [
    'pushover', 'bike', 'bugle', 'cashregister', 'classical', 'cosmic', 'falling', 'gamelan', 'incoming',
    'intermission', 'magic', 'mechanical', 'pianobar', 'siren', 'spacealarm', 'tugboat', 'alien', 'climb',
    'persistent', 'echo', 'updown', 'vibrate', 'none'
];
export const ALERT_TARGETS = [ 'SLACK', 'DISCORD', 'PUSHOVER', 'EMAIL' ];
// The activity and alert log -- every line esback has narrated to Slack, kept
// as rows so the record outlives the channel. Read-only, so there is no
// ADMIN_HISTORY_FORM to go with it: nothing here is edited, and a history a
// person can rewrite is not a history.
export const ADMIN_HISTORY_LIST = ADMIN + "historyList";
// The six graphs and the five diagnostics of es-metrics.md.
export const ADMIN_METRICS = ADMIN + "metrics";

// Mailings -- a note to a group of accounts, and the two templates that go out
// when somebody registers or saves their account. A list and a form, the shape
// the talks pair takes, because a mailing is written and re-read rather than
// typed once.
export const ADMIN_MAILING_LIST = ADMIN + "mailingList";
export const ADMIN_MAILING_FORM = ADMIN + "mailingForm";

// Rulesys -- the games a pack is written for and a campaign plays. One is the
// catch-all, Homebrew, which the dropdowns default to and a remove never takes.
export const ADMIN_RULESYS_LIST = ADMIN + "rulesysList";
export const ADMIN_RULESYS_FORM = ADMIN + "rulesysForm";

// Importers -- the sources the codex is filled from, the SRD first. Each row is a config
// esback loads from resources/importers at startup; Run imports it on the server.
export const ADMIN_IMPORTER_LIST = ADMIN + "importerList";
export const ADMIN_IMPORTER_FORM = ADMIN + "importerForm";
export const ADMIN_PACK_REVIEW = ADMIN + "packReview";
// Subscriptions: every account's sub start, end, and status, with +1 month and +1 year per row.
export const ADMIN_SUBS_LIST = ADMIN + "subsList";

export const ADMIN_CODEX = ADMIN + "codex/";
// The codex as ONE admin screen: a section rail beside the entry table. It
// replaces the Entries and Sections menu buttons, which were two screens over
// one body of content. Both of those routes still resolve -- nothing links to
// them any more, but a bookmark is not a reason to 404 somebody.
export const ADMIN_CODEX_LIST = ADMIN_CODEX + "list";
export const ADMIN_ENTRY_FORM = ADMIN_CODEX + "entryForm";
export const ADMIN_ENTRY_LIST = ADMIN_CODEX  + "entryList";
export const ADMIN_SECTION_FORM = ADMIN_CODEX  + "sectionForm";
export const ADMIN_SECTION_LIST = ADMIN_CODEX  + "sectionList";
export const ADMIN_COMMENT_FORM = ADMIN_CODEX  + "commentForm";
export const ADMIN_COMMENT_LIST = ADMIN_CODEX  + "commentList";
export const ADMIN_TAG_FORM = ADMIN_CODEX  + "tagForm";
export const ADMIN_TAG_LIST = ADMIN_CODEX  + "tagList";

// ⚠️ DEAD, and kept only so this note has somewhere to live. Nothing routes
// /action/contactSend and nothing ever did: the contact form's Submit was an
// anchor pointing here, which is why pressing it landed on the 404 page. The
// form posts to API_CONTACT now. Remove this the next time somebody is in here
// and can check that ACTION itself still has a caller.
export const ACTION_CONTACT_SEND = ACTION + "contactSend";

export const API_AUTH_USER = API + "authUser";
export const API_LOGIN = API + "login";
export const API_LOGOUT = API + "logout";
export const API_HEALTH = API + "health";

// What THIS ENVIRONMENT looks like -- light or dark, the accent, the surface.
// GET is open to anybody, including a logged-out visitor, because the app has
// to paint before anyone has logged in; PUT is ROLE_ADMIN. See
// EsConfigController on esback and EsThemeContext.js here.
export const API_CONFIG_THEME = API + "configTheme";
// One site default cell, admin only; and the signed-in account's own cells.
// The home page trailer's settings: a public read, and a write per key (admin or the token).
export const API_CONFIG_SITE = API + "configSite";
export const API_CONFIG_KEYS = API + "configKeys";
export const API_CONFIG_THEME_COLOR = API_CONFIG_THEME + "/color";
export const API_ACCOUNT_COLOR = API + "accountColor";
// Colorsheets: named looks anyone may pick. Picking one for yourself is under accountColor,
// making one the site's default under configTheme -- see EsColorsheetController.
export const API_COLORSHEET = API + "colorsheet";
export const API_COLORSHEET_DEFAULT = API_COLORSHEET + "/default";
export const API_COLORSHEET_SOURCES = API_COLORSHEET + "/sources";
export const API_ACCOUNT_COLOR_SHEET = API_ACCOUNT_COLOR + "/sheet";
export const API_CONFIG_THEME_SHEET = API_CONFIG_THEME + "/sheet";

// Commit hashes appear in exactly one place, the collapsed commit list on the
// changelog, and only an admin sees them as links.
export const GITHUB_COMMIT = "https://github.com/alderloons/escapade/commit/";

// The changelog's screenshots: public objects at <release>/<file> below this,
// one bucket for every environment. Named by each release's manifest in
// es_release.screenshots, which zdchangelog writes; the bucket is never listed.
export const SCREENSHOT_URL = "https://s3.amazonaws.com/escapade.gg/screenshots/";

export const API_ACCOUNT = API + "account";
export const API_ACCOUNT_LIST = API + "accountList";
// The packs an account owns, which its delete takes: EsConstants.API_ACCOUNT_PACKS on esback.
export const API_ACCOUNT_PACKS = API + "accountPacks";
// The packs an account's delete hides rather than deletes, and who built on them (V126):
// EsConstants.API_ACCOUNT_FORKED_PACKS on esback, bare for the caller's own, /{id} for an admin.
// NM_HIDE_FORKED is the yes, in the self-delete's body and on the roster's DELETE.
export const API_ACCOUNT_FORKED_PACKS = API + "accountForkedPacks";
export const NM_HIDE_FORKED = "hideForked";
// The limited groups and the admin's move: EsConstants.API_GROUP and
// API_ACCOUNT_GROUP on esback. GET group lists them, PUT group/<role> sets a
// limit, PUT accountGroup/<id> moves one account.
export const API_GROUP = API + "group";
export const API_ACCOUNT_GROUP = API + "accountGroup";

// ---------------------------------------------------------------------------
// The account -- what a person may change about THEMSELVES. Mirrors
// EsConstants.API_ACCOUNT / API_ACCOUNT_OWN / API_ACCOUNT_PASSWORD on esback; change
// one, change both.
//
// A GET of bare API_ACCOUNT is the CALLER'S OWN, with no id, and that is the only
// way this screen can start: /api/v1/authUser answers with a name and the Cognito
// groups and never with the row, so nothing here knows the account's id until this
// call hands it over. Every write below is addressed by that id, at API_ACCOUNT_OWN
// because API_ACCOUNT/{id} is the admin's whole-entity read and write.
//
// The password is a flat sibling rather than API_ACCOUNT_OWN + "/password" because
// the two have different rules -- the account is self-or-admin, the password is
// self only -- and on esback a matcher on a nested name could not have told them
// apart. See EsAccountController.
// ---------------------------------------------------------------------------
export const API_ACCOUNT_OWN = API + "accountOwn";
export const API_ACCOUNT_PASSWORD = API + "accountPassword";
// The caller's own locale for copy and mail (es_account.locale): PUT { locale }, a BCP-47 tag.
export const API_ACCOUNT_LOCALE = API + "accountLocale";
// The caller's own "I can translate" locale groups (es_account.translate_offer): PUT { tags }.
export const API_ACCOUNT_TRANSLATE_OFFER = API + "accountTranslateOffer";
// The caller's own picture: POST multipart (file), DELETE, and GET for the bytes.
// Copykeys (rolloutEsTranslations): GET API_COPY + "/" + tag is one locale's bundle, resolved along
// its group's fallback chain by esback; API_COPY_LOCALES lists the groups and their coverage.
export const API_COPY = API + "copy";
export const API_COPY_LOCALES = API_COPY + "/locales";
// The copykeys admin, ROLE_ADMIN by esback's matcher on copy/admin. A variant is addressed by
// its locale tag, and a tag with no group yet gets one on the first write.
export const API_COPY_ADMIN = API_COPY + "/admin";
export const API_COPY_ADMIN_VARIANT = ( pId, pTag ) => API_COPY_ADMIN + "/" + pId + "/variant/" + encodeURIComponent( pTag );
export const API_COPY_ADMIN_GROUP = API_COPY_ADMIN + "/group";
// esback's EsCopyService.COPY_LIVE_COVERAGE: a group's own share at or past it lists it in the
// nav's language menu.
export const COPY_LIVE_COVERAGE = 0.95;
// Translators (es-i18n.md, phase 7): /translate's queues and writes, ROLE_TRANSLATOR or
// ROLE_ADMIN by esback's matcher, the group checked on every call; the translator admin is
// ROLE_ADMIN's; the credits are anybody's. esback's EsTranslateController spells the same paths.
export const VIEW_TRANSLATE = "/translate";
export const API_TRANSLATE = API + "translate";
export const API_TRANSLATE_ME = API_TRANSLATE + "/me";
export const API_TRANSLATE_CREDIT = API_TRANSLATE + "/credit";
export const API_TRANSLATE_QUEUE = ( pTag ) => API_TRANSLATE + "/queue/" + encodeURIComponent( pTag );
export const API_TRANSLATE_KEY = ( pTag, pKeyId ) => API_TRANSLATE + "/" + encodeURIComponent( pTag ) + "/" + pKeyId;
export const API_TRANSLATE_SUGGESTION = ( pId ) => API_TRANSLATE + "/suggestion/" + pId;
export const API_TRANSLATOR_ADMIN = API + "translator/admin";
export const API_TRANSLATOR_ADMIN_INVITE = API_TRANSLATOR_ADMIN + "/invite";
// An invite's link is VIEW_TRANSLATE?invite=<token>, which presses this as the signed-in reader.
export const API_TRANSLATE_ACCEPT_INVITE = API_TRANSLATE + "/accept-invite";
export const NM_INVITE = "invite";
export const API_COPY_CREDITS = API_COPY + "/credits";
export const API_COPY_ADMIN_SUGGESTIONS = ( pId ) => API_COPY_ADMIN + "/" + pId + "/suggestions";
export const API_ACCOUNT_AVATAR = API + "accountAvatar";
// Delete your own account (EsAccountController.deleteOwnAccount): POST the typed address.
export const API_ACCOUNT_DELETE = API + "accountDelete";
// The nicknames nobody may choose and the sentence refusing one: esback's EsNicknames.
export const API_RESERVED_NICKNAMES = API + "reservedNicknames";
// The history page envelope -- { content, totalElements, ... }, newest first.
//
// Takes page and size, plus the five the admin screen's filter row drives:
// channel, level, source, actor and message. The first three are matched
// EXACTLY and the last two as contains, which is the same split entryList
// draws between its picked and its typed columns. There is no happenedAt
// parameter and there is not meant to be -- a contains-match against a date is
// not a date filter. See the header of EsHistoryList and EsHistoryRepo.
//
// The SAME path answers the Source dropdown's values when FACETS_PARAM is
// present. That is not tidiness: esback's EsAuthConfig names this route exactly
// and closes it to ROLE_ADMIN, and a flat sibling -- historyFacets, the way the
// codex spells it -- would match no rule in that chain and fall through to
// "any logged-in account". See EsHistoryController.
export const API_HISTORY = API + "history";
// The Alerts screen's rows and each row's Fire press. ROLE_ADMIN, named in EsAuthConfig;
// spelled the same as esback's API_ALERT_ADMIN.
export const API_ALERT_ADMIN = API + "alert/admin";
export const API_ALERT_ADMIN_FIRE = ( pId ) => API_ALERT_ADMIN + "/" + pId + "/fire";
// ROLE_ADMIN, named in EsAuthConfig. Spelled the same in esback's EsConstants.
export const API_METRICS = API + "metrics";
export const API_REGISTER = API + "register";

// "Forgot Password": an address goes in, and what comes back says which of
// three things happened rather than saying it in a sentence -- a mail is on its
// way, the account signs in with Google, or nothing here uses that address.
//
// The CODE is the server's and the SENTENCE is this side's, which is the split
// EsOauthMessage argues for at length: the wording depends on what the page
// around it already says, and the server does not know that. See EsForgot.js,
// where the three sentences live.
//
// Anonymous, because somebody who cannot log in is the only caller it will ever
// have. It is still a browser write, so it goes through esPost and carries the
// XSRF token like every other one.
export const API_PASSWORD_FORGOT = API + "passwordForgot";

// The second half: the token out of the mailed link, and the password chosen on
// the strength of it. Anonymous for the same reason -- the token IS the
// credential, which is why it is minted, mailed and spent rather than being
// anything the browser could have named itself.
export const API_PASSWORD_RESET = API + "passwordReset";

// Asked before the reset page draws a form: is this link still good? A read --
// it spends nothing -- sent as a POST only so the token stays out of logged URLs.
export const API_PASSWORD_RESET_CHECK = API + "passwordResetCheck";

// The mailing list -- the join box on the marketing page. A soft register:
// a name and an address land as an account at role MAILING, with no password
// and no Cognito user behind them. See EsJoinService on esback, which also
// says why this endpoint answers a new address and a known one identically.
export const API_JOIN = API + "join";

// "Tell us what you think" -- the contact box on the marketing page, mailed to
// every administrator. Until 2026-09-17 the Submit button was an anchor to
// ACTION_CONTACT_SEND below, a route nothing serves, so pressing it landed on
// escapade's own 404 page and nothing was ever sent.
export const API_CONTACT = API + "contact";

// The home page video played, or opened on YouTube, once each per page load (EsHomeVideo.js,
// esback's EsHomeVideoController).
export const API_HOME_VIDEO = API + "homeVideo";
export const HOME_VIDEO_PLAYED = "played";
export const HOME_VIDEO_YOUTUBE = "youtube";

// The invitation code, POSTed BEFORE the browser leaves for Cognito's hosted
// UI. It is the only moment a code can be checked on a path that carries no
// body of ours: the federated round trip is a redirect, and a redirect has
// nowhere to put a form field. esback stashes the TIER it grants on the
// session, and EsLoginSuccessHandler spends it when the browser comes back.
// See EsAuthController.acceptInvite.
export const API_INVITE = API + "invite";

// The Login with Google twin of API_INVITE: posted first, it names the page the
// login returns to, or clears it. See EsAuthController.loginDestination.
export const API_LOGIN_DESTINATION = API + "loginDestination";

// The landing's UTM and click IDs, posted before every registration (EsFirstTouch.js,
// esback's EsFirstTouchController).
export const API_FIRST_TOUCH = API + "first-touch";

// The birthdate step's answer: finishes the login esback is holding (EsAuthController.finishLogin).
export const API_LOGIN_BIRTHDATE = API + "loginBirthdate";

// The minimum age to hold an account, mirrored from esback's EsAge.MIN_AGE, which alone
// decides. Read by the terms page's sentence only: no form checks it, so none hints at it.
export const MIN_AGE = 13;

// Spring Security's own authorization endpoint, not one of ours -- which is why
// it sits outside the /api/v1/ block above and why the string is duplicated
// from esback's EsConstants.OAUTH2_AUTHORIZATION_COGNITO rather than derived.
// "cognito" is the registration id in application.yaml
// (spring.security.oauth2.client.registration.cognito), so the two move
// together or the button 404s.
//
// Reached through googleAuthUrl in EsEnv.js, which builds it from serverUrl.
// The webpack dev server was a second origin until 2026-09-23 and a bare
// "/oauth2/..." answered index.html there, failing on a laptop only; the dev
// server proxies the path now, the way the ALB and compose's nginx do.
export const OAUTH2_AUTHORIZATION_COGNITO = "/oauth2/authorization/cognito";

// ---------------------------------------------------------------------------
// Mailings. One stem plus its list, the shape the talks and characters pairs
// take, plus two that are not CRUD.
//
// mailingSend PRESSES SEND, and is a POST for that reason: it does something in
// the world rather than describing a change to a row. It takes the mailing's
// id and no body -- what goes out is what is STORED, so the form saves before
// it sends and a body here would be a second, disagreeing answer to "what was
// actually mailed".
//
// mailingOptions is what the audience dropdown and the tag help line are built
// from. Asked of the server rather than written down here, so neither can drift
// from EsMailingAudience and EsMailingTags -- the same argument entryFacets
// makes for the codex's picked columns.
//
// Every one of these is ROLE_ADMIN, and the two GETs are named explicitly in
// esback's EsAuthConfig: a GET under /api/ otherwise falls through to "somebody
// logged in", and a mailing is readable before it is sent.
// ---------------------------------------------------------------------------
export const API_MAILING = API + "mailing";
export const API_MAILING_LIST = API + "mailingList";
export const API_MAILING_SEND = API + "mailingSend";
export const API_MAILING_OPTIONS = API + "mailing/options";

// One mailing rendered exactly as it will arrive, for the Preview button.
//
// The html is built on esback by the SAME method the message body is built by,
// and that is the whole point: a React component drawing "the same" header
// would be a second renderer, and the two would agree until somebody changed
// one of them -- at which point the preview would be confidently wrong, which
// is worse than no preview at all. See EsMailingService.preview.
export const API_MAILING_PREVIEW = ( pId ) => API + "mailing/" + pId + "/preview";

export const API_ENTRY = API + "entry";
export const API_ENTRY_KEY = API + "entry/key";
export const API_ENTRY_FIRST = API + "entryFirst";
// { recent, popular }: the caller's last five entries read (empty with no login) and the ten
// most read by everyone, a pack's page among them; each row carries `library`, true for a page
// that opens at VIEW_PACKS/<packId>/<key> and not at VIEW_CODEX/<key>.
export const API_ENTRY_VISITS = API + "entryVisits";
// What the codex's two picked columns can be -- every kind and every author
// any entry actually carries. Asked of the database rather than written down,
// so a kind that arrives with an import is offered the moment it exists.
export const API_ENTRY_FACETS = API + "entryFacets";
export const API_ENTRY_LIST = API + "entryList";
// An entry's images, by the entry's key and the image's name: the list and the upload
// at API_ENTRY_IMAGE/<key>, the bytes, rename, and delete at API_ENTRY_IMAGE/<key>/<name>.
// The markdown names one as `image:<name>` (IMAGE_REF), which the codex resolves here.
export const API_ENTRY_IMAGE = API + "entryImage";
export const IMAGE_REF = "image:";
export const API_SECTION = API + "section";
export const API_SECTION_LIST = API + "sectionList";
export const API_SECTION_TREE = API + "sectionTree";
export const API_COMMENT = API + "comment";
export const API_COMMENT_LIST = API + "commentList";
export const API_COMMENT_LIST_BY_ENTRY = API + "commentListByEntry/";
export const API_TAG = API + "tag";
export const API_TAG_LIST = API + "tagList";
export const API_TAG_LIST_BY_ENTRY = API + "tagListByEntry/";
export const API_TAG_CLOUD = API + "tagCloud";
// Trailing slash on the second: the caller appends an entry key. GET reads the state, PUT flips it.
export const API_BOOKMARK_LIST = API + "bookmarkList";
export const API_BOOKMARK = API + "bookmark/";
// Trailing slash: the caller appends a section key. One section's index chips (EsIndexTags).
export const API_SECTION_TAGS = API + "sectionTags/";
// Trailing slash, like API_TAG_LIST_BY_ENTRY above, because the caller appends
// the value: this one the encoded tag name, that one the entry key.
export const API_ENTRY_LIST_BY_TAG = API + "entryListByTag/";

export const API_ENTRY_SEARCH = API + "entrySearch";
export const QUERY_PARAM = "?q=";
export const QUOTE = "\"";
export const MIN_SEARCH_LENGTH = 3;

// ---------------------------------------------------------------------------
// Characters. One controller, so one stem plus its list -- which is also what
// esback's EsFlagFilter.CHARACTER_PREFIXES gates: everything under /api/v1/
// beginning "character".
//
// characterList is the caller's OWN characters and takes no owner parameter.
// Whose they are is the session's to decide, never the query string's.
//
// characterDerive is the builder's: a POST of a character that has not been
// saved, answering the derived block a saved one carries on every response. It
// exists because EsCharacterNew writes no row until Create and still has to
// show a modifier under a score as it is chosen.
//
// characterName is the builder's too: a GET of a batch of species-matched
// name suggestions, generated in esback from one grammar. It is a batch rather
// than a single name so a player pressing Suggest again gets the next one out
// of the response instead of making another request.
// ---------------------------------------------------------------------------
export const API_CHARACTER = API + "character";
export const API_CHARACTER_LIST = API + "characterList";
export const API_CHARACTER_DERIVE = API + "characterDerive";
export const API_CHARACTER_NAME = API + "characterName";
export const API_CHARACTER_ROLL = API + "characterRoll";
// characterScores is the builder's step for a rulesys's own way of setting
// attributes (builder.scoreMethods): what each pick offers and what the choices
// so far give, worked out in esback so the builder computes no rule.
export const API_CHARACTER_SCORES = API + "characterScores";

// ---------------------------------------------------------------------------
// Campaigns. One stem plus its list, the same shape as the characters pair
// above -- and the same thing esback's EsFlagFilter.DASHBOARD_PREFIXES gates:
// everything under /api/v1/ beginning "campaign".
//
// campaignList is the caller's OWN campaigns, from their membership rows, and
// takes no owner parameter. Whose they are is the session's to decide.
// ---------------------------------------------------------------------------
export const API_CAMPAIGN = API + "campaign";
export const API_CAMPAIGN_LIST = API + "campaignList";
// The demo table's id and key, behind switchEsDemoMode (EsDemoController).
export const API_DEMO = API + "demo";

// Membership, phase 3. Three stems, and every one of them still begins
// "campaign" so the same DASHBOARD_PREFIXES gate covers them all.
//
// campaignJoin is FLAT -- no id is appended, ever. The campaign is resolved
// from the code in the body, which is the whole point of a join code: the
// person holding it does not know the campaign's id and must not need to.
export const API_CAMPAIGN_JOIN = API + "campaignJoin";
// Rotate (POST) and close (DELETE) share one stem and one id. GM only, both.
export const API_CAMPAIGN_JOIN_CODE = API + "campaignJoinCode";
// One member SEAT by id -- not a campaign id and not an address. The server
// reads the campaign off the seat row, so the client never says which campaign
// a member update is for.
export const API_CAMPAIGN_MEMBER = API + "campaignMember";

// The game console. Seven more stems, and every one of them still begins
// "campaign" -- so DASHBOARD_PREFIXES covers all seven with no entry anywhere,
// which is exactly what EsFlagFilter's javadoc anticipated: "later phases add
// the session, note and combatant stems to this list as their controllers
// arrive. A campaign is the parent of all three, so they belong to this gate
// and not to a second one."
//
// Every one of them is addressed by a CAMPAIGN id first, including the two that
// then name a combatant. The tier is a fact about a campaign and a caller, so a
// path that named a combatant alone could not refuse anybody.
export const API_CAMPAIGN_SESSION = API + "campaignSession";
export const API_CAMPAIGN_COMBATANT = API + "campaignCombatant";
export const API_CAMPAIGN_TURN = API + "campaignTurn";
export const API_CAMPAIGN_VITALS = API + "campaignVitals";
export const API_CAMPAIGN_ROLL = API + "campaignRoll";
export const API_CAMPAIGN_ACTION = API + 'campaignAction';
// What the acting row may do now, and a press on a flow's waiting step (es-homebrew-engine.md §3.5, §4).
export const API_CAMPAIGN_CAN = API + 'campaignCan';
export const API_CAMPAIGN_FLOW = API + 'campaignFlow';
export const API_CAMPAIGN_REST = API + "campaignRest";
export const API_CAMPAIGN_SLOT = API + "campaignSlot";
export const API_CAMPAIGN_STATS = API + "campaignStats";
export const API_CAMPAIGN_AWARD = API + "campaignAward";
// Take a waiting level: the GM for anybody, a player for their own character.
export const API_CAMPAIGN_ADVANCE = API + "campaignAdvance";
export const API_CAMPAIGN_REVIVE = API + "campaignRevive";
// Cast a spell off the caster's sheet, and roll a save a spell left waiting on a
// character's row: the GM for anybody, a player for their own character.
export const API_CAMPAIGN_CAST = API + "campaignCast";
export const API_CAMPAIGN_SAVE = API + "campaignSave";

// The chat, which is the one game write that is not the GM's: everybody at the
// table may say something. See EsGameController.say.
export const API_CAMPAIGN_CHAT = API + "campaignChat";

// The GM's screen: a campaign's notes, every route the GM's. See EsNoteController.
export const API_CAMPAIGN_NOTE = API + "campaignNote";

// The Notes screen: the caller's own notes at a table they sit at. See EsPersonalNoteController.
export const API_CAMPAIGN_PERSONAL_NOTE = API + "campaignPersonalNote";

// The dice tray. NOT campaignRoll, which resolves a check against a DC from a
// die the GM types; this one produces numbers for a table with no dice in reach
// and resolves nothing. See EsGameController.rollDice.
export const API_CAMPAIGN_DICE = API + "campaignDice";

// Roll initiative for a character, which seats them in the fight. The GM's for
// anybody, a player's for their own. See EsGameController.rollInitiative.
export const API_CAMPAIGN_INITIATIVE = API + "campaignInitiative";

// ---------------------------------------------------------------------------
// Flatscreen maps. The stem is `map`, and it is the first thing on this screen
// that does NOT begin "campaign" -- which is a decision rather than an
// oversight, so here is the reason.
//
// Every stem above is addressed by a CAMPAIGN id first, because the tier is a
// fact about a campaign and a caller and a path that named a combatant alone
// could refuse nobody. A map is not like that. The list a reader picks from is
// the campaign's own maps PLUS the shared samples, and a sample belongs to no
// campaign at all -- so `campaignMap/<campaignId>/<mapId>` would be a lie for
// half of what it addresses, and an id that has to be qualified by a campaign
// it does not belong to is an id that gets qualified wrongly.
//
// So the two halves split by what they actually take. The LIST takes a campaign
// id, because "which maps may this reader see" is a question about a campaign.
// The IMAGE takes a map id alone, because the row itself says whether it is a
// sample or whose campaign it is.
// ---------------------------------------------------------------------------
export const API_MAP = API + "map";
export const API_MAP_LIST = API + "mapList";
// Map chips: GM for anyone, a player for their own character; creatures are the GM's.
export const API_MAP_CHIP = API + "mapChip";
// Casting the Fullscreen map to a TV (EsCastController): the GM's cast/token/{campaignId}
// (POST mints, DELETE stops), and the TV's renew, view, and layer, read with the cast token.
export const API_CAST_TOKEN = API + "cast/token";
export const API_CAST_RENEW = API + "cast/renew";
export const API_CAST_VIEW = API + "cast/view";
export const API_CHARACTER_AVATAR = ( pId ) => API + "character/" + pId + "/avatar";
// A character's items (T9): characterItem/{characterId}, then /{itemId} for one.
export const API_CHARACTER_ITEM = API + "characterItem";
// The sheet's spell picker (T13), under its character as EsSpellPickerController has it.
export const API_CHARACTER_SPELLS = ( pId ) => API + "character/" + pId + "/spells";
export const API_CHARACTER_SPELL_OPTIONS = ( pId ) => API + "character/" + pId + "/spellOptions";
export const API_CHARACTER_SPELL_PREPARE = ( pId ) => API + "character/" + pId + "/spells/prepare";
export const API_CHARACTER_SPELL_SWAP_DONE = ( pId ) => API + "character/" + pId + "/spells/swapDone";
export const API_CHARACTER_SPELL_LEVEL_UP = ( pId ) => API + "character/" + pId + "/spells/levelUp";

// The Commons, the out-of-game chat: topics by group in the left dock, one topic's stream in the
// right. Both routes take the topic's key -- t<id>, or c<campaignId> for a
// table's topic, which is its console chat -- and chatPost the post id after
// it for an edit or a delete. EsFlagFilter gates both on the stem "chat".
export const VIEW_CHAT = VIEW + "commons";
export const API_CHAT_TOPIC = API + "chatTopic";
export const API_CHAT_POST = API + "chatPost";
// Guardian: squelch and unsquelch (EsGuardianController), a moderator's writes.
export const API_GUARDIAN = API + "guardian";
export const API_GUARDIAN_SQUELCH = API_GUARDIAN + "/squelch";
export const API_GUARDIAN_UNSQUELCH = API_GUARDIAN + "/unsquelch";
export const API_GUARDIAN_REPORT = API_GUARDIAN + "/report";
export const API_GUARDIAN_GM_SQUELCH = API_GUARDIAN + "/gmSquelch";
export const API_GUARDIAN_HELD = API_GUARDIAN + "/held";
export const API_GUARDIAN_RELEASE = API_GUARDIAN_HELD + "/release";
// The moderation view: open reports beside squelch and unsquelch, for an admin or an alpha.
export const VIEW_MODERATION = VIEW + "moderation";
export const API_CHAT_READ = API + "chatRead";
// The Commons' people (esback's EsCommonsController): Members, Friends, Messages, the profile card.
export const API_CHAT_MEMBERS = API + "chatMembers";
export const API_CHAT_FRIEND = API + "chatFriend";
export const API_CHAT_MESSAGE = API + "chatMessage";
export const API_CHAT_PROFILE = API + "chatProfile";

// The nav bar's bell (esback's EsNotifyController): the list, /count for the poll, /<id>/read and
// /read to mark, and /mentionable/<key> for the composer's @. Gated with the Chat (rolloutEsChat).
export const API_NOTIFICATION = API + "notification";

// Looking for Group in the Commons (rolloutEsLfg; plans/es-lfg-plan-2026-10-25.md): the Tables
// screen and one post's Table screen. A static segment, so react-router ranks it above the
// topic's :id, and no topic key (t<n>, c<n>) can ever be spelled "tables". EsFlagFilter gates
// the stem "lfg" on esback.
export const VIEW_COMMONS_TABLES = VIEW_CHAT + "/tables";
export const POST_KEY_PARAM = "/:postKey";
export const API_LFG_POST = API + "lfgPost";
export const API_LFG_POST_CAMPAIGN = API_LFG_POST + "/campaign";
export const API_LFG_APPLICATION = API + "lfgApplication";
export const API_LFG_REPORT = API + "lfgReport";
export const API_LFG_BLOCK = API + "lfgBlock";

export const API_HELLO = API + "hello";
export const API_AUTH_HELLO = API + "authHello";
export const API_POST_HELLO = API + "postHello";

export const API_HELLO_REST = API + "helloRest";
export const API_POST_HELLO_REST = API + "postHelloRest";

// ---------------------------------------------------------------------------
// Codex at scale -- added 2026-09-01 with the SRD import (plan §5.3).
//
// The codex went from 3 entries to ~1,140, and every list in it is now a
// server-paginated one. These are the query parameter names the paginated
// endpoints take (srd-api-contract-2026-09-01.md §1, §3, §4) and the page sizes
// this app asks for.
//
// PAGE IS 0-BASED here, because Spring's Pageable is: page=0 is the first page,
// and the response echoes it back as "number". PrimeReact 11's paginator is
// 1-BASED. The conversion happens at exactly one place, EsCodexPagedList, and
// nowhere else -- see the comment there, because getting this wrong is the kind
// of bug that renders fine and shows the wrong page.
// ---------------------------------------------------------------------------
export const PAGE_PARAM = "page";
export const SIZE_PARAM = "size";
export const SORT_PARAM = "sort";
export const SECTION_PARAM = "section";
// The forms (EsForm, V068): every list of the console and the builder is the
// entries carrying a tag of one form, a tagged section's entries included.
export const FORM_MONSTER = "monster";
export const FORM_SPELL = "spell";
export const FORM_WEAPON = "weapon";
export const FORM_CLASS = "class";
export const FORM_SUBCLASS = "subclass";
export const FORM_SPECIES = "species";
export const FORM_BACKGROUND = "background";
export const FORM_PARAM = "form";
export const API_FORM_LIST = API + "formList";
export const SEARCH_PARAM = "q";
// Asks an endpoint for the values its filter dropdowns can offer, rather than
// for rows. Only /api/v1/history takes it -- the codex has its own route for
// the same job, API_ENTRY_FACETS above, and the reason these two differ is an
// authorization rule rather than a preference; the comment over API_HISTORY
// has it. The value is never read, only its presence.
export const FACETS_PARAM = "facets";

// 25 rows of title-plus-excerpt is about a screen and a half on a phone, which
// is the reading unit Tim asked for -- players on their phones at the table.
export const CODEX_PAGE_SIZE = 25;
// The contract's own default for entrySearch. Said out loud rather than left to
// the server so the "showing 1-20 of 214" line can be drawn before the first
// response comes back.
export const SEARCH_PAGE_SIZE = 20;

// A search fires on every keystroke, and at 339 spells a common stem like "fir"
// is a real query. Long enough that typing a word is one request rather than
// five, short enough that it still feels live.
export const SEARCH_DEBOUNCE_MS = 250;
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Codex packs. PACK_PARAM scopes a codex read: a comma list of pack ids is the
// packs the reader picked, and absent is every pack the reader can see.
// ---------------------------------------------------------------------------
// The rulesys: rulesysList is the dropdowns' public read; rulesys is the admin
// screen's, with use counts, and the writes. A remove takes ?moveTo=<id>.
export const API_RULESYS = API + "rulesys";
export const API_RULESYS_LIST = API + "rulesysList";
// A rulesys's definition view, GET rulesysView/{id}: labels, order, and which
// columns, tabs, panels, and steps exist. 404 while rolloutEsHomebrewEngine is off.
export const API_RULESYS_VIEW = API + "rulesysView";
export const API_RULESYS_STATS = API + 'rulesysStats';
export const API_RULESYS_TREE = API + 'rulesysTree';
export const API_RULESYS_FORK = API + 'rulesysFork';
export const API_RULESYS_DRAFT = API + 'rulesysDraft';
export const API_RULESYS_VALIDATE = API + 'rulesysValidate';
export const API_RULESYS_PREVIEW = API + 'rulesysPreview';
export const API_RULESYS_PUBLISH = API + 'rulesysPublish';
// The editor's Diff tab, GET rulesysDraft/{id}/diff: the saved draft, the row's own published
// keys, and what it inherits (es-homebrew-engine.md §5).
export const RULESYS_DIFF = '/diff';
// The modules and templates a fork starts from, and Make this a module, POST
// rulesysModule/{id} (es-homebrew-engine.md §5). Empty while rolloutEsHomebrewEngine is off.
export const API_RULESYS_MODULES = API + 'rulesysModules';
export const API_RULESYS_MODULE = API + 'rulesysModule';
export const API_RULESYS_TEMPLATES = API + 'rulesysTemplates';
// Versions (es-homebrew-engine.md §3.9): GET rulesysVersions, every rulesystem's latest number;
// GET rulesysVersions/{id}, one's list. A campaign's pin: GET campaignRulesysVersion/{id}, POST
// and DELETE .../{id}/pin, POST .../{id}/upgrade (GM only). 404 while rolloutEsHomebrewEngine is off.
export const API_RULESYS_VERSIONS = API + 'rulesysVersions';
export const API_CAMPAIGN_RULESYS_VERSION = API + 'campaignRulesysVersion';
// The editor's Simulate panel and Play a turn, POST rulesysSimulate/{id}: a sandbox run of the
// draft that writes nothing (es-homebrew-engine.md §4). And a chat line's trace, GET
// campaignTrace/{messageId}, the GM's "Why". Both 404 while rolloutEsHomebrewEngine is off.
export const API_RULESYS_SIMULATE = API + 'rulesysSimulate';
export const API_CAMPAIGN_TRACE = API + 'campaignTrace';

// The Rules section: the tree of rulesys at /view/rules, one draft's editor
// at /view/rules/:id. Behind rolloutEsHomebrewEngine (EsRulesysGate).
export const VIEW_RULES = VIEW + "rules";
export const API_CAMPAIGN_TIMER = API + 'campaignTimer';
// The importers: list, CRUD, and <id>/run and <id>/stop. ROLE_ADMIN throughout.
export const API_IMPORTER = API + "importer";

export const API_PACK = API + "pack";
export const API_PACK_LIST = API + "packList";
export const API_PACK_LIBRARY = API + "packLibrary";
// The reader's pack picks (V062): GET says whether they are an account's, PUT stores them.
export const API_PACK_PICKS = API_PACK + "/picks";
// The account's order of the packs it sees (V064): PUT every one, top to bottom.
export const API_PACK_RANK = API_PACK + "/rank";
// A table's Players' lists (T93): GET the GM's panel, PUT one list of one pack handed or not.
export const API_PACK_HAND = ( pCampaignId ) => API_PACK + "/hand/" + pCampaignId;
// The Library's review (V062): GET the admin queue; PUT pack/<id>/review decides one.
export const API_PACK_REVIEW_QUEUE = API_PACK + "/review";
// How many wait, for the Admin menu's Review badge. Admin only, as the queue is.
export const API_PACK_REVIEW_COUNT = API_PACK_REVIEW_QUEUE + "/count";
// Where a reader with no account keeps their pack picks, for the tab. Logout clears it.
export const CODEX_PICKS_STORAGE = "esCodexPackPicks";
// Set by the account delete before it leaves for home, read once by the page it lands on.
export const ACCOUNT_DELETED_STORAGE = "esAccountDeleted";
// The packs the TOC rail hides (V071); a reader with no account keeps them in this browser.
export const API_PACK_TOC_HIDE = API_PACK + "/tocHide";
export const CODEX_TOC_HIDE_STORAGE = "esCodexTocHidden";
// es-homebrew-engine.md §7: the Library's Rulesystems tab and the composed notices (Legal, and
// packNotices/<id> for one pack), both public; pack/<id>/license (GET, PUT) and pack/<id>/fork
// (POST), a pack's; pack/rules/<rulesysId> (POST), a published rulesystem of the caller's as its pack.
export const API_PACK_RULESYSTEMS = API + "packRulesystems";
export const API_PACK_NOTICES = API + "packNotices";
export const API_PACK_RULES = API_PACK + "/rules";
export const PACK_PARAM = "pack";

// ---------------------------------------------------------------------------
// Subscriptions. Stripe's return_url is VIEW_SUBSCRIBE_DONE with
// SESSION_ID_PARAM, built by esback, so the two spellings move together.
// A campaign create or session start by a non-subscriber answers 402 with
// code SUB_REQUIRED.
// ---------------------------------------------------------------------------
export const VIEW_SUBSCRIBE = VIEW + "subscribe";
export const VIEW_SUBSCRIBE_DONE = VIEW_SUBSCRIBE + "/done";
export const SESSION_ID_PARAM = "session_id";
export const API_SUBS = API + "subs";
export const API_SUBS_ME = API_SUBS + "/me";
export const API_SUBS_CHECKOUT = API_SUBS + "/checkout";
export const API_SUBS_ADMIN = API_SUBS + "/admin";
export const API_SUBS_ADMIN_RENEW = ( pAccountId ) => API_SUBS_ADMIN + "/" + pAccountId + "/renew";
export const API_SUBS_AUTO_RENEW_OFF = API_SUBS + "/autoRenew/off";
export const SUB_REQUIRED = "es-sub-required";

// ---------------------------------------------------------------------------
// Finances. The summary is public; finances/admin is ROLE_ADMIN.
// ---------------------------------------------------------------------------
export const API_FINANCES = API + "finances";
export const API_FINANCES_SUMMARY = API_FINANCES + "/summary";
export const API_FINANCES_EXPENSES = API_FINANCES + "/admin/expenses";
export const API_FINANCES_REVENUE = API_FINANCES + "/admin/revenue";
export const ADMIN_EXPENSE_LIST = ADMIN + "expenseList";
export const ADMIN_EXPENSE_FORM = ADMIN + "expenseForm";
// The Storyboard page (V194): the list of boards, and one board edited in place; every route ROLE_ADMIN.
export const ADMIN_STORYBOARD = ADMIN + "storyboard";
export const API_STORYBOARD = API + "storyboard";
export const API_STORYBOARD_ZIP = API_STORYBOARD + "/zip";
export const API_STORYBOARD_ROW = API_STORYBOARD + "/row";
export const API_STORYBOARD_IMPORT = API_STORYBOARD + "/import";
// A table's scene: the GM's Play, Pause, and Stop, and the boards the GM may play. The seats read it on the console poll.
export const API_CAMPAIGN_SCENE = API + "campaignScene";
// The trailer of an LFG listing, by post key: a redirect to the signed file.
export const API_LFG_TRAILER = API + "lfgTrailer";
export const ADMIN_REVENUE_LIST = ADMIN + "revenueList";
export const ADMIN_PROFIT = ADMIN + "profit";
export const API_BOT_COMMANDS = API + "botCommands";
export const API_BOT_POLL = API + "botPoll";

// The shared AI service (es-ai-service.md): every route begins "ai", switchEsAi's stem. A task's
// route is API_AI + "/" + its camelCase name (probe, builderNames); EsAi.js builds it.
export const API_AI = API + "ai";
export const API_AI_USAGE = API_AI + "/usage";
export const API_AI_OPT_IN = API_AI + "/optIn";
export const API_AI_REPORT = API_AI + "/report";
export const API_AI_BUILDER_ACCEPT = ( pPartId ) => API_AI + "/builder/part/" + pPartId + "/accept";
export const API_AI_BUILDER_NOTE_ACCEPT = ( pNoteId ) => API_AI + "/builder/note/" + pNoteId + "/accept";
export const API_AI_ADMIN = API_AI + "/admin";
export const API_AI_ADMIN_USAGE = API_AI_ADMIN + "/usage";
export const API_AI_ADMIN_BUDGET = API_AI_ADMIN + "/budget";
export const API_AI_ADMIN_CEILING = API_AI_ADMIN + "/ceiling";
export const ADMIN_AI = ADMIN + "ai";
export const API_BOT_BRIDGE = API + "botBridge";
export const API_BOT_BRIDGE_LINK = API + "botBridgeLink";
export const API_BOT_IDENTITY = API + "botIdentity";

// ---------------------------------------------------------------------------
// The campaign builder, behind rolloutEsCampaignBuilder. EsFlagFilter refuses
// the stem "adventure" while it is off. adventure/{id} is an adventure pack's
// plan and parts, adventureLink/{id} its links, adventurePlay/{id} a
// campaign's adventure, by the campaign's id.
// ---------------------------------------------------------------------------
export const API_ADVENTURE = API + "adventure";
export const API_ADVENTURE_ID = ( pPackId ) => API_ADVENTURE + "/" + pPackId;
export const API_ADVENTURE_PART = ( pPackId ) => API_ADVENTURE_ID( pPackId ) + "/part";
export const API_ADVENTURE_PART_ID = ( pPackId, pPartId ) => API_ADVENTURE_PART( pPackId ) + "/" + pPartId;
export const API_ADVENTURE_ORDER = ( pPackId ) => API_ADVENTURE_ID( pPackId ) + "/order";
// adventure/{id}/fork: a Library adventure copied into the caller's own, run at { campaignId } when named.
export const API_ADVENTURE_FORK = ( pPackId ) => API_ADVENTURE_ID( pPackId ) + "/fork";
export const API_ADVENTURE_LINK = ( pPackId ) => API + "adventureLink/" + pPackId;
export const API_ADVENTURE_LINK_ID = ( pPackId, pLinkId ) => API_ADVENTURE_LINK( pPackId ) + "/" + pLinkId;
export const API_ADVENTURE_PLAY = ( pCampaignId ) => API + "adventurePlay/" + pCampaignId;
export const API_ADVENTURE_PLAY_PREP = ( pCampaignId ) => API_ADVENTURE_PLAY( pCampaignId ) + "/prep";
export const API_ADVENTURE_PLAY_PART = ( pCampaignId, pPartId ) => API_ADVENTURE_PLAY( pCampaignId ) + "/part/" + pPartId;
export const API_ADVENTURE_PLAY_UNDO = ( pCampaignId, pMark ) => API_ADVENTURE_PLAY( pCampaignId ) + "/undo/" + pMark;
export const API_ADVENTURE_PLAY_PLAN = ( pCampaignId ) => API_ADVENTURE_PLAY( pCampaignId ) + "/plan";
export const API_ADVENTURE_PLAY_REWARD = ( pCampaignId, pPartId ) => API_ADVENTURE_PLAY( pCampaignId ) + "/reward/" + pPartId;
export const API_ADVENTURE_PLAY_HANDOUT = ( pCampaignId, pPartId ) => API_ADVENTURE_PLAY( pCampaignId ) + "/handout/" + pPartId;
// The party's story (§3.4), read by anybody at the table; the reveal's presses are the GM's.
export const API_ADVENTURE_PLAY_STORY = ( pCampaignId ) => API_ADVENTURE_PLAY( pCampaignId ) + "/story";
export const API_ADVENTURE_PLAY_REVEAL = ( pCampaignId, pPartId ) => API_ADVENTURE_PLAY( pCampaignId ) + "/reveal/" + pPartId;
export const API_ADVENTURE_PLAY_REVEAL_NEXT = ( pCampaignId, pPartId ) => API_ADVENTURE_PLAY( pCampaignId ) + "/revealNext/" + pPartId;
// A map pin (§2.2a): the author's pins and the maps they may go on (?campaignId=), and a table's (?mapId=).
export const API_ADVENTURE_PIN = ( pPackId ) => API_ADVENTURE_ID( pPackId ) + "/pin";
export const API_ADVENTURE_PIN_ID = ( pPackId, pPinId ) => API_ADVENTURE_PIN( pPackId ) + "/" + pPinId;
export const API_ADVENTURE_PLAY_PIN = ( pCampaignId, pMapId ) => API_ADVENTURE_PLAY( pCampaignId ) + "/pin?mapId=" + pMapId;
// The Story page, on the campaign's slug as the console is: VIEW_CAMPAIGN + KEY_PARAM + STORY_SUFFIX
// is the route, VIEW_STORY( slug ) a link to it.
export const STORY_SUFFIX = "/story";
export const VIEW_STORY = ( pCampaignKey ) => VIEW_CAMPAIGN + "/" + pCampaignKey + STORY_SUFFIX;
export const API_ADVENTURE_PLAY_DRAW = ( pCampaignId, pPartId ) => API_ADVENTURE_PLAY( pCampaignId ) + "/draw/" + pPartId;
export const API_ADVENTURE_PLAY_CLOCK = ( pCampaignId, pPartId ) => API_ADVENTURE_PLAY( pCampaignId ) + "/clock/" + pPartId;
// adventureCheck/{id}: the checker's findings over an adventure pack; ?campaignId= adds a table's party and play.
export const API_ADVENTURE_CHECK = ( pPackId ) => API + "adventureCheck/" + pPackId;
export const API_ADVENTURE_OUTLINE = ( pPackId ) => API_ADVENTURE_ID( pPackId ) + "/outline";
// adventure/{id}/template/{key} applies a template into an adventure pack; adventureTemplateList lists them.
export const API_ADVENTURE_TEMPLATE = ( pPackId, pKey ) => API_ADVENTURE_ID( pPackId ) + "/template/" + pKey;
export const API_ADVENTURE_TEMPLATE_LIST = API + "adventureTemplateList";
// The builder, a page under one campaign by its SLUG, as the campaign page and
// the console are: VIEW_CAMPAIGN + KEY_PARAM + ADVENTURE_SUFFIX is the route,
// VIEW_ADVENTURE( key ) a link to it.
export const ADVENTURE_SUFFIX = "/adventure";
export const VIEW_ADVENTURE = ( pCampaignKey ) => VIEW_CAMPAIGN + "/" + pCampaignKey + ADVENTURE_SUFFIX;
// The builder's wizard (es-campaign-builder.md §4.2), under the builder's own path.
export const WIZARD_SUFFIX = "/wizard";
export const VIEW_ADVENTURE_WIZARD = ( pCampaignKey ) => VIEW_ADVENTURE( pCampaignKey ) + WIZARD_SUFFIX;

// ---------------------------------------------------------------------------
// The game API (es-unreal-plugin.md §4.1), behind rolloutEsUnrealPlugin: the
// versioned surface engines call, beside API. esback's EsConstants spells the
// same stems; EsFlagFilter refuses the stem "game" while the flag is off.
// ---------------------------------------------------------------------------
export const API_GAME = "/api/game/v1/";
export const API_GAME_ME = API_GAME + "me";
export const API_GAME_CAMPAIGN_LIST = API_GAME + "campaignList";
export const API_GAME_SESSION = API_GAME + "session";
export const API_GAME_RULESYS = API_GAME + "rulesys";
export const API_GAME_MAP = API_GAME + "map";
export const API_GAME_CODEX = API_GAME + "codex";
export const API_GAME_PACK = API_GAME + "pack";
export const API_GAME_COPY = API_GAME + "copy";
export const API_GAME_MEMORY = API_GAME + "memory";
export const GM_ONLY = "es-gm-only";
export const NOT_YOUR_CHARACTER = "es-not-your-character";
export const OFF_TURN = "es-off-turn";
export const NO_SESSION = "es-no-session";
export const FEATURE_OFF = "es-feature-off";
export const RATE_LIMITED = "es-rate-limited";
export const API_GAME_CHIP = API_GAME + "chip";
export const API_GAME_LOOT = API_GAME + "loot";
export const API_GAME_GIVE = API_GAME + "give";
export const IDEMPOTENCY_KEY = "Idempotency-Key";
export const IDEMPOTENT_REPLAYED = "Idempotent-Replayed";
export const KEY_REUSED = "es-key-reused";

// How a game signs in (es-unreal-plugin.md §4.2): the /link page a person types a device's
// code on, and the account page's Devices tab. Both call /api/v1/, where no game token is
// read; esback's EsConstants spells the same paths.
export const VIEW_LINK = "/link";
export const TAB_DEVICES = "devices";
export const API_ACCOUNT_DEVICE = API + "accountDevice";
export const API_DEVICE_LINK = API + "deviceLink";
export const API_DEVICE_LINK_DENY = API_DEVICE_LINK + "/deny";
// The developer program (es-unreal-plugin.md §6.1): the account page's Game clients tab and the
// developer agreement under /legal. esback's EsConstants spells the same paths.
export const TAB_CLIENTS = "clients";
export const API_ACCOUNT_CLIENT = API + "accountClient";
export const API_DEVELOPER_AGREEMENT = API + "developerAgreement";
export const VIEW_DEVELOPER_AGREEMENT = VIEW_LEGAL + "/developer";
export const BIRTHDATE_NEEDED = "es-birthdate-needed";
export const ACCOUNT_DISABLED = "es-account-disabled";
